import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { adminApi, type Product, type Category } from "@/lib/admin-api"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select"
import { ArrowLeft, Loader2, Upload, X, Star } from "lucide-react"
import { toast } from "sonner"
import { cn } from "@/lib/utils"

export default function AdminProductForm() {
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditing = Boolean(id)

    const [isLoading, setIsLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [categories, setCategories] = useState<Category[]>([])
    const [product, setProduct] = useState<Product | null>(null)

    const [formData, setFormData] = useState({
        name: "",
        description: "",
        price: "",
        category: "",
        stock: "0",
        available: true,
    })
    const [imageFile, setImageFile] = useState<File | null>(null)
    const [imagePreview, setImagePreview] = useState<string | null>(null)

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true)
            try {
                const categoriesData = await adminApi.getCategories()
                setCategories(categoriesData)

                if (isEditing && id) {
                    const productData = await adminApi.getProduct(Number(id))
                    setProduct(productData)
                    setFormData({
                        name: productData.name,
                        description: productData.description,
                        price: productData.price,
                        category: String(productData.category),
                        stock: String(productData.stock),
                        available: productData.available,
                    })
                    if (productData.image_url) {
                        setImagePreview(productData.image_url)
                    }
                }
            } catch (error) {
                console.error("Error:", error)
                toast.error("Ma'lumotlarni yuklashda xatolik")
            } finally {
                setIsLoading(false)
            }
        }
        fetchData()
    }, [id, isEditing])

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            setImageFile(file)
            setImagePreview(URL.createObjectURL(file))
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.name || !formData.price || !formData.category) {
            toast.error("Iltimos, barcha majburiy maydonlarni to'ldiring")
            return
        }

        setIsSaving(true)
        try {
            const form = new FormData()
            form.append("name", formData.name)
            form.append("description", formData.description)
            form.append("price", formData.price)
            form.append("category", formData.category)
            form.append("stock", formData.stock)
            form.append("available", String(formData.available))

            if (imageFile) {
                form.append("image", imageFile)
            }

            if (isEditing && id) {
                await adminApi.updateProduct(Number(id), form)
                toast.success("Mahsulot yangilandi")
            } else {
                await adminApi.createProduct(form)
                toast.success("Mahsulot yaratildi")
            }
            navigate("/admin/products")
        } catch (error: any) {
            console.error("Error:", error)
            toast.error(error.message || "Xatolik yuz berdi")
        } finally {
            setIsSaving(false)
        }
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-muted-foreground">Yuklanmoqda...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate("/admin/products")}>
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        {isEditing ? "Mahsulotni tahrirlash" : "Yangi mahsulot"}
                    </h1>
                    <p className="text-muted-foreground">
                        {isEditing ? product?.name : "Yangi mahsulot qo'shish"}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Main Info */}
                    <div className="lg:col-span-2 space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Asosiy ma'lumotlar</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Nomi *</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="Mahsulot nomi"
                                        className="h-12"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="description">Tavsif</Label>
                                    <Textarea
                                        id="description"
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Mahsulot haqida batafsil ma'lumot..."
                                        rows={5}
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Rasm</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {imagePreview ? (
                                        <div className="relative w-full max-w-md">
                                            <img
                                                src={imagePreview}
                                                alt="Preview"
                                                className="w-full h-64 object-cover rounded-xl"
                                            />
                                            <Button
                                                type="button"
                                                variant="destructive"
                                                size="icon"
                                                className="absolute top-2 right-2"
                                                onClick={() => {
                                                    setImageFile(null)
                                                    setImagePreview(null)
                                                }}
                                            >
                                                <X className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-slate-300 dark:border-gray-700 rounded-xl cursor-pointer hover:border-emerald-500 transition-colors">
                                            <Upload className="h-12 w-12 text-muted-foreground mb-4" />
                                            <span className="text-muted-foreground">Rasm yuklash uchun bosing</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                className="hidden"
                                            />
                                        </label>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Kategoriya</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Select
                                    value={formData.category}
                                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                                >
                                    <SelectTrigger className="h-12">
                                        <SelectValue placeholder="Kategoriya tanlang" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {categories.map((category) => (
                                            <SelectItem key={category.id} value={String(category.id)}>
                                                {category.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Narx va ombor</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="price">Narxi (so'm) *</Label>
                                    <Input
                                        id="price"
                                        type="number"
                                        value={formData.price}
                                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                        placeholder="0"
                                        className="h-12"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="stock">Omborda (dona)</Label>
                                    <Input
                                        id="stock"
                                        type="number"
                                        value={formData.stock}
                                        onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                                        placeholder="0"
                                        className="h-12"
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Holati</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-medium">Faol</p>
                                        <p className="text-sm text-muted-foreground">Saytda ko'rsatish</p>
                                    </div>
                                    <Switch
                                        checked={formData.available}
                                        onCheckedChange={(checked) => setFormData({ ...formData, available: checked })}
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        <Button
                            type="submit"
                            className="w-full h-12 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <>
                                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                    Saqlanmoqda...
                                </>
                            ) : (
                                isEditing ? "Saqlash" : "Yaratish"
                            )}
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    )
}
